import { useEffect, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import type { Highlight, Track } from '../../api/types'
import { kacheloptionen } from '../../lib/kartenquelle'
import { useBuchquelle } from './buchquelle'
import { nadelbild } from '../map/nadelbild'

/**
 * Die Übersichtskarte des Buches — Route und nummerierte Punkte.
 *
 * **Eine eigene, stumme Karte und nicht die aus der Anwendung.** Jene trägt
 * Werkzeug, Regler und Gesten; hier wird nichts angetippt, sondern gedruckt.
 * Was sie hat und die andere nicht: sie sagt Bescheid, **wenn die Kacheln da
 * sind** (`onFertig`). Ohne das druckt man ein weißes Feld — der Druck wartet
 * nicht auf Bilder aus dem Netz.
 *
 * Die Kacheln holt wie überall der Browser beim Kartenserver, nicht der
 * Server. Das ist die erste der drei Ausnahmen und hier keine neue.
 */
export function Buchkarte({
  tracks,
  punkte,
  onFertig,
}: {
  tracks: Track[]
  punkte: Highlight[]
  onFertig: () => void
}) {
  const kasten = useRef<HTMLDivElement | null>(null)
  const quelle = useBuchquelle()

  /*
   * **Die Daten und die Meldung liegen in Refs, nicht in den Abhängigkeiten.**
   *
   * Hier standen sie als Abhängigkeiten, und der erste Anlauf war deshalb
   * kaputt: `tracks`, `punkte` und `onFertig` sind bei jedem Zeichnen neu, der
   * Effekt lief also erneut -- räumte die Karte im Aufräumer ab und stieg dann
   * an der `gebaut`-Sperre sofort wieder aus. Übrig blieb ein leerer Kasten,
   * und weil dabei auch die Uhr gelöscht wurde, meldete er nie „fertig": der
   * Druckknopf blieb für immer zu. Gemessen im Prüfstand, bevor es jemand am
   * Gerät sehen konnte.
   *
   * Gebaut wird deshalb genau einmal je **Schlüssel** -- was die Karte zeigt,
   * ändert sich in einem Buch ohnehin nicht mehr.
   */
  const daten = useRef({ tracks, punkte, onFertig })
  daten.current = { tracks, punkte, onFertig }
  const gebautFuer = useRef<string | null>(null)

  const schluessel = useMemo(
    () =>
      `${quelle.tile_url}|${tracks.map((t) => t.id).join(',')}|${punkte.map((p) => p.id).join(',')}`,
    [quelle.tile_url, tracks, punkte],
  )

  useEffect(() => {
    const { tracks, punkte, onFertig } = daten.current
    if (kasten.current === null) return
    // Erst bauen, wenn es etwas zu zeigen gibt -- eine leere Karte im Buch
    // wäre eine Seite über nichts.
    if (tracks.length === 0 && punkte.length === 0) return
    if (gebautFuer.current === schluessel) return
    gebautFuer.current = schluessel

    const karte = L.map(kasten.current, {
      zoomControl: false,
      attributionControl: true,
      // Nichts davon wird gebraucht: gedruckt wird ein Standbild.
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      keyboard: false,
    })

    // `crossOrigin`, so that the still image for the book may copy the tiles (`mapImage.ts`).
    const ebene = L.tileLayer(quelle.tile_url, { ...kacheloptionen(quelle), crossOrigin: 'anonymous' })

    /*
     * **Fertig heißt: die Kacheln sind da.** `load` feuert, wenn alle
     * sichtbaren geladen sind; `tileerror` zählt hier auch als erledigt, denn
     * eine graue Kachel ist ein Ergebnis und kein Warten. Die Uhr darüber ist
     * die Sicherung: antwortet der Kartenserver gar nicht, soll der Druck
     * nicht für immer gesperrt bleiben.
     */
    let gemeldet = false
    const melde = () => {
      if (gemeldet) return
      gemeldet = true
      onFertig()
    }
    ebene.on('load', melde)
    const uhr = window.setTimeout(melde, 12000)
    ebene.addTo(karte)

    // Eine Aufzeichnung besteht aus Stücken (`segments`), und jeder Punkt ist
    // `[Breite, Länge, Sekunden]` -- die Sekunden braucht das Buch nicht.
    const ecken: L.LatLngExpression[] = []
    for (const spur of tracks) {
      for (const stueck of spur.segments) {
        const reihe = stueck.points.map((p): L.LatLngExpression => [p[0], p[1]])
        if (reihe.length > 1) {
          L.polyline(reihe, { color: '#2b6cb0', weight: 3 }).addTo(karte)
        }
        ecken.push(...reihe)
      }
    }

    punkte.forEach((punkt, i) => {
      ecken.push([punkt.lat, punkt.lon])
      L.marker([punkt.lat, punkt.lon], {
        icon: L.divIcon({
          /*
           * **Dieselbe Tropfenform wie in der Anwendung** (`nadelbild()`),
           * mit der Nummer darin. Hier stand die nackte Zahl in einem
           * `<div>`, und eine Regel dazu gab es nirgends -- `buch__nadel`
           * kam im ganzen Baum nur an dieser Stelle vor. Am Gerät standen
           * deshalb Ziffern auf der Karte und keine Nadeln.
           *
           * **Leer heißt leer, nicht Vorgabe.** Bekommt `className` einen
           * leeren Text, lässt Leaflet sein `leaflet-div-icon` weg -- und
           * damit den weißen Kasten mit grauem Rand, den es sonst um jedes
           * `divIcon` malt. Die Karte der Anwendung macht es genauso.
           */
          className: '',
          html: nadelbild('buch__nadel', { nummer: i + 1 }),
          // Die Spitze zeigt auf den Ort, nicht die Mitte des Bildes --
          // vorher saß die Zahl mittig über dem Punkt und log um fünfzehn
          // Punkte.
          iconSize: [22, 30],
          iconAnchor: [11, 30],
        }),
        title: punkt.title,
        alt: punkt.title,
      }).addTo(karte)
    })

    if (ecken.length > 0) {
      karte.fitBounds(L.latLngBounds(ecken), { padding: [24, 24] })
    } else {
      karte.setView([51.25, 7.19], 10)
    }

    return () => {
      window.clearTimeout(uhr)
      karte.remove()
      gebautFuer.current = null
    }
  }, [schluessel])

  if (tracks.length === 0 && punkte.length === 0) return null

  return <div className="buch__karte" ref={kasten} />
}
